import { chipClasses } from '@mui/material/Chip';

import type { Theme } from '../types';
import type { Components } from '@mui/material/styles';

function getSoftVars(color: string, theme: Theme): any {
  return {
    ...theme.applyStyles('light', {
      '--Chip-softBg': `var(--mui-palette-${color}-backgroundSoft)`,
      '--Chip-softColor': `var(--mui-palette-${color}-text)`,
      '--Chip-softDisabledBg': `var(--mui-palette-${color}-backgroundSoft)`,
      '--Chip-softDisabledColor': `var(--mui-palette-${color}-text)`,
      '--Chip-softHoverBg': `var(--mui-palette-${color}-backgroundSoftHover)`,
    }),
    ...theme.applyStyles('dark', {
      '--Chip-softBg': `var(--mui-palette-${color}-backgroundSoft)`,
      '--Chip-softColor': `var(--mui-palette-${color}-text)`,
      '--Chip-softDisabledBg': `var(--mui-palette-${color}-backgroundSoft)`,
      '--Chip-softDisabledColor': `var(--mui-palette-${color}-text)`,
      '--Chip-softHoverBg': `var(--mui-palette-${color}-backgroundSoftHover)`,
    }),
  };
}

export const MuiChip = {
  defaultProps: {
    color: 'secondary', // default will be removed in material v6
    variant: 'soft',
  },
  styleOverrides: {
    root: { borderRadius: '16px', fontWeight: 600 },
    outlined: ({ theme }) => {
      // Custom case for secondary, the rest is handled by the theme
      return {
        borderColor: 'var(--mui-palette-secondary-200)',
        color: 'var(--mui-palette-secondary-900)',
        ...theme.applyStyles('dark', {
          borderColor: 'var(--mui-palette-secondary-700)',
          color: 'var(--mui-palette-secondary-50)',
        }),
      };
    },
    soft: () => {
      return {
        backgroundColor: 'var(--Chip-softBg)',
        color: 'var(--Chip-softColor)',
        [`& .${chipClasses.deleteIcon}`]: {
          color: 'var(--Chip-softDeleteIconColor)',
          '&:hover': { color: 'var(--Chip-softDeleteIconColor)' },
          '@media (hover: hover)': {
            '&:hover': { color: 'var(--Chip-softDeleteIconHoverColor)' },
          },
        },
        '&.Mui-focusVisible': { backgroundColor: 'var(--Chip-softHoverBg)' },
        variants: [
          {
            props: ({ ownerState }) => ownerState.disabled,
            style: {
              backgroundColor: 'var(--Chip-softDisabledBg)',
              color: 'var(--Chip-softDisabledColor)',
            },
          },
          {
            props: ({ ownerState }) => ownerState.clickable,
            style: {
              '&:hover': { backgroundColor: 'var(--Chip-softBg)' },
              '@media (hover: hover)': {
                '&:hover': { backgroundColor: 'var(--Chip-softHoverBg)' },
              },
            },
          },
        ],
      };
    },
    softPrimary: ({ theme }) => {
      return getSoftVars('primary', theme);
    },
    softSecondary: ({ theme }) => {
      return getSoftVars('secondary', theme);
    },
    softSuccess: ({ theme }) => {
      return getSoftVars('success', theme);
    },
    softInfo: ({ theme }) => {
      return getSoftVars('info', theme);
    },
    softWarning: ({ theme }) => {
      return getSoftVars('warning', theme);
    },
    softError: ({ theme }) => {
      return getSoftVars('error', theme);
    },
    iconSmall: { fontSize: 'var(--icon-fontSize-sm)' },
    iconMedium: { fontSize: 'var(--icon-fontSize-md)' },
  },
} satisfies Components<Theme>['MuiChip'];
